import React, { useCallback, useEffect, useRef, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { MemoizedHighchart } from "components/reports/Chart/MemoizedHighchart";
import { useChartHook } from "components/reports/Chart/useChartHook";
import { InfoCard } from "components/reports/Info-card/InfoCard";
import { HighchartsReactRefObject } from "highcharts-react-official";
import { Chart as ChartDataType, CubicData } from "store/types";
import {
  AICardData,
  CardData,
  CustomConfig,
  DataTableCardData,
  KPICardData,
  SummaryCardData,
  TextCardData
} from "store/types";

interface ViewProps {
  data?: CardData;
  config?: CustomConfig;
  title?: string;
}

export const SummaryCard: React.FC<ViewProps> = ({ data, title }) => {
  // Handle new measure-summary API response format
  const summaryData = data as SummaryCardData | undefined;

  // Get the title from measureName, parameter, or fallback to title prop
  const displayTitle = summaryData?.measureName || summaryData?.parameter || summaryData?.title || title;

  // Check if we have the new format with all stats
  const hasFullStats = summaryData?.total !== undefined && summaryData?.average !== undefined;

  // If no data configured, show placeholder
  if (!summaryData || (summaryData.total === undefined && summaryData.value === undefined)) {
    return (
      <Box sx={{ p: 1, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>Click Settings to configure summary</Typography>
      </Box>
    );
  }

  // If we have full stats (new API format), show all values
  // Values come as pre-formatted strings from the API
  if (hasFullStats) {
    return (
      <Box
        sx={{
          p: 1.5,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "auto"
        }}
      >
        {displayTitle && (
          <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: "0.9rem", color: "#071F35", textAlign: "center" }}>
            {displayTitle} Summary
          </Typography>
        )}
        <Stack
          direction="row"
          sx={{
            flex: 1,
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <Box sx={{ textAlign: "center", minWidth: 70, p: 1, bgcolor: "#f8fafc", borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "0.65rem", color: "#64748b", textTransform: "uppercase" }}>Total</Typography>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold", color: "#1e293b" }}>
              {summaryData.total}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center", minWidth: 70, p: 1, bgcolor: "#f8fafc", borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "0.65rem", color: "#64748b", textTransform: "uppercase" }}>Average</Typography>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold", color: "#1e293b" }}>
              {summaryData.average}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center", minWidth: 70, p: 1, bgcolor: "#f8fafc", borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "0.65rem", color: "#64748b", textTransform: "uppercase" }}>Min</Typography>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold", color: "#1e293b" }}>{summaryData.min}</Typography>
          </Box>
          <Box sx={{ textAlign: "center", minWidth: 70, p: 1, bgcolor: "#f8fafc", borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "0.65rem", color: "#64748b", textTransform: "uppercase" }}>Max</Typography>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold", color: "#1e293b" }}>{summaryData.max}</Typography>
          </Box>
        </Stack>
      </Box>
    );
  }
};

const getCellValue = (row: DataTableCardData["data"][number], colKey: string, colIdx: number): string => {
  if (row && typeof row === "object" && "valueData" in row) {
    if (colIdx === 0 || colKey === "name") return row.title || "";
    const value = row.valueData?.[colKey];
    return value !== null && value !== undefined ? value.toLocaleString() : "N/A";
  }
  const value = row?.[colKey];
  if (typeof value === "number") return value.toLocaleString();
  return String(value || "").slice(0, 20);
};

export const DataTable: React.FC<ViewProps> = ({ data }) => {
  const tableData = data as DataTableCardData | undefined;
  const columns: string[] = (tableData?.columns || []).map((col) => (typeof col === "string" ? col : col.title));
  const columnKeys: string[] = (tableData?.columns || []).map((col) => (typeof col === "string" ? col : col.key));
  const rows = tableData?.data || [];

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  if (!tableData || (!columns.length && !rows.length)) {
    return (
      <Box sx={{ p: 1, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>Click Settings to configure table</Typography>
      </Box>
    );
  }

  const hasAnyChildren = rows.some((row) => row.children?.length);

  const renderRows = (rowList: DataTableCardData["data"], depth: number, parentKey: string) => {
    const result: React.ReactNode[] = [];

    rowList.forEach((row, rowIdx) => {
      const rowKey = `${parentKey}-${rowIdx}`;
      const hasChildren = !!row.children?.length;
      const isExpanded = expanded.has(rowKey);
      const isGroupRow = hasChildren && depth < 2;

      result.push(
        <tr key={rowKey} style={{ backgroundColor: isGroupRow ? "#F6F8FC" : rowIdx % 2 === 0 ? "white" : "#FAFBFC" }}>
          {columnKeys.map((colKey, colIdx) => {
            const cellValue = getCellValue(row, colKey, colIdx);
            const isFirstCol = colIdx === 0;
            return (
              <td
                key={colIdx}
                title={cellValue}
                style={{
                  padding: "4px 8px",
                  paddingLeft: isFirstCol ? `${8 + depth * 16}px` : "8px",
                  fontSize: "0.65rem",
                  color: "#071F35",
                  borderBottom: "1px solid #F0F4F8",
                  textAlign: isFirstCol ? "left" : "right",
                  fontWeight: isFirstCol && isGroupRow ? 600 : isFirstCol ? 500 : 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: isFirstCol ? 120 : 80
                }}
              >
                {isFirstCol && hasChildren ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                    <IconButton onClick={() => toggleExpand(rowKey)} sx={{ p: 0, width: 18, height: 18, minWidth: 0 }}>
                      {isExpanded ? (
                        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                    <span
                      style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
                      onClick={() => toggleExpand(rowKey)}
                    >
                      {cellValue}
                    </span>
                  </Box>
                ) : (
                  <span style={{ paddingLeft: hasAnyChildren && !hasChildren ? "20px" : undefined }}>{cellValue}</span>
                )}
              </td>
            );
          })}
        </tr>
      );

      if (hasChildren && isExpanded) {
        result.push(...(renderRows(row.children!, depth + 1, rowKey) as React.ReactNode[]));
      }
    });

    return result;
  };

  return (
    <Stack sx={{ p: "10px", height: "100%", flexDirection: "column", gap: "10px" }}>
      {!!columns.length && !!rows.length ? (
        <Box
          sx={{
            overflow: "auto",
            flex: 1,
            border: "1px solid #DBE8F5",
            borderRadius: "8px",
            bgcolor: "white"
          }}
        >
          <table style={{ borderCollapse: "collapse", minWidth: "100%" }}>
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    title={col}
                    style={{
                      padding: "6px 8px",
                      textAlign: idx === 0 ? "left" : "right",
                      fontWeight: 500,
                      fontSize: "0.65rem",
                      color: "#071F35",
                      backgroundColor: "#F6F8FC",
                      borderBottom: "1px solid #DBE8F5",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: idx === 0 ? 120 : 80,
                      position: "sticky",
                      top: 0,
                      zIndex: 1
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{renderRows(rows, 0, "root")}</tbody>
          </table>
        </Box>
      ) : (
        <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>No data available</Typography>
      )}
    </Stack>
  );
};

interface ChartViewProps extends ViewProps {
  groupBy?: string;
  width?: number;
  height?: number;
}

export const ChartView: React.FC<ChartViewProps> = ({ data, groupBy = "deal", width = 300, height = 200 }) => {
  const chartRef = useRef<HighchartsReactRefObject>(null);

  // Update chart size when props change (during card resize)
  useEffect(() => {
    if (chartRef.current?.chart && width > 0 && height > 0) {
      chartRef.current.chart.setSize(width, height, { duration: 0 });
    }
  }, [width, height]);

  // Cast data to Chart type from the API
  const chartData = data as ChartDataType | undefined;

  // No-op click handler since we're in report builder mode
  const handleGraphClick = () => {};

  // Calculate responsive pie size based on container dimensions
  const pieSize = Math.min(width, height) * 0.45;

  const chartOptions = useChartHook({
    chartData,
    setFieldValue: handleGraphClick,
    groupBy: groupBy,
    chart: {
      height: height,
      width: width,
      spacing: [10, 10, 30, 10],
      reflow: true
    },
    plotOptions: {
      pie: {
        size: pieSize,
        center: ["50%", "40%"]
      }
    },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      floating: false,
      maxHeight: 40,
      itemStyle: {
        fontSize: "9px",
        fontWeight: "normal"
      },
      margin: 8,
      padding: 5,
      y: 0
    }
  });

  if (!chartData || !chartData.data) {
    return (
      <Box sx={{ p: 1, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>Click Settings to configure chart</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        overflow: "hidden"
      }}
    >
      <MemoizedHighchart key={`chart-${width}-${height}`} options={chartOptions} chartRef={chartRef} />
    </Box>
  );
};

export const KPICard: React.FC<ViewProps> = ({ data }) => {
  // Handle API response format (ICubicData) - cubic1, cubic2, cubic3, etc.
  const rawData = data as KPICardData | undefined;

  // Extract KPI entries - filter out empty/undefined cubics from API response
  const kpiEntries: Array<{ name: string; data: CubicData }> = [];

  if (rawData) {
    const cubicMap: Array<{ key: keyof KPICardData; name: string }> = [
      { key: "cubic1", name: "cubic1" },
      { key: "cubic2", name: "cubic2" },
      { key: "cubic3", name: "cubic3" },
      { key: "cubic4", name: "cubic4" },
      { key: "cubic5", name: "cubic5" }
    ];
    cubicMap.forEach(({ key, name }) => {
      const cubic = rawData[key];
      if (cubic && cubic.title && cubic.value !== undefined && cubic.value !== null && cubic.value !== "") {
        kpiEntries.push({
          name,
          data: cubic
        });
      }
    });
  }

  if (kpiEntries.length === 0) {
    return (
      <Box sx={{ p: 1, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>Click Settings to configure KPIs</Typography>
      </Box>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        p: 1,
        height: "100%",
        gap: "10px",
        "& .info-card": {
          minWidth: 0,
          padding: "10px",
          "& .info-card__title": {
            fontSize: "11px",
            lineHeight: "14px"
          },
          "& .info-card__data": {
            fontSize: "16px",
            lineHeight: "22px"
          }
        }
      }}
    >
      {kpiEntries.map((kpi) => (
        <InfoCard
          key={kpi.name}
          name={kpi.name}
          title={kpi.data.title}
          value={kpi.data.value}
          filter={kpi.data.filter || kpi.data.title}
          tooltip={kpi.data.tooltip}
          pageType="cubicSnapshot"
          isLoading={false}
          configurable={false}
          apply_number_format={kpi.data.apply_number_format}
        />
      ))}
    </Stack>
  );
};

export const TextCard: React.FC<ViewProps> = ({ data }) => {
  const textData = data as TextCardData | undefined;
  const content = textData?.content;

  return (
    <Box sx={{ p: 1, height: "100%", display: "flex", flexDirection: "column", overflow: "auto" }}>
      {content ? (
        <Typography
          sx={{
            fontSize: "0.8rem",
            color: "#1e293b",
            lineHeight: 1.6,
            flex: 1,
            whiteSpace: "pre-wrap"
          }}
        >
          {content}
        </Typography>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>Click Settings to add text content</Typography>
        </Box>
      )}
    </Box>
  );
};

interface AICardProps extends ViewProps {
  question?: string;
  answer?: string;
}

export const AICard: React.FC<AICardProps> = ({ data, question, answer: savedAnswer }) => {
  const aiData = data as AICardData | undefined;
  // Prefer saved answer from customConfig, fallback to data
  const answer = savedAnswer || aiData?.answer || "";

  return (
    <Box sx={{ p: 1, height: "100%", display: "flex", flexDirection: "column", overflow: "auto" }}>
      {question && (
        <Typography sx={{ fontWeight: "bold", mb: 1, fontSize: "0.85rem", color: "#071F35", lineHeight: 1.3 }}>
          {question}
        </Typography>
      )}
      {answer ? (
        <Box
          sx={{ fontSize: "0.8rem", color: "#1e293b", lineHeight: 1.6, flex: 1 }}
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>Click Settings to ask AI a question</Typography>
        </Box>
      )}
    </Box>
  );
};
